//https://www.npmjs.com/package/ngx-indexed-db

import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { from, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { CreateObjectStore, openDatabase } from './ngx-indexed-db';
import { createTransaction, optionsGenerator, validateBeforeTransaction } from './ngx-indexed-db-utils';
import { CONFIG_TOKEN, DBConfig, DBMode, Key, ObjectStoreMeta } from './ngx-indexed-db.meta';

@Injectable({
  providedIn: 'root',
})
export class SiaNgxIndexedDBService {
  #dbConfig = inject<DBConfig>(CONFIG_TOKEN);
  #platformId = inject(PLATFORM_ID);

  readonly #isBrowser: boolean;
  #indexedDB: IDBFactory = <IDBFactory>{};
  #recoveryPromise: Promise<void> | null = null;

  constructor() {
    this.#isBrowser = isPlatformBrowser(this.#platformId);
    this.createDataBase();
  }
  /**
   * Opens DB and ensures the given store exists; if missing (e.g., DB wiped), it recreates schema once and retries.
   */
  async #openDatabaseEnsuringStore(storeName: string, mode: DBMode): Promise<IDBDatabase> {
    try {
      const db = await openDatabase(this.#indexedDB, this.#dbConfig.name, this.#dbConfig.version);
      if (!db.objectStoreNames.contains(storeName)) {
        await db.close();
        await this.#recoverDatabaseSchema();
        const db2 = await openDatabase(this.#indexedDB, this.#dbConfig.name, this.#dbConfig.version);
        if (!db2.objectStoreNames.contains(storeName)) {
          throw new Error(`objectStore does not exists after recovery: ${storeName}`);
        }
        return db2;
      }
      return db;
    } catch (err) {
      throw err;
    }
  }

  async #recoverDatabaseSchema(): Promise<void> {
    // If recovery is already in progress, wait for it to complete
    if (this.#recoveryPromise) {
      return this.#recoveryPromise;
    }

    this.#recoveryPromise = this.#doRecoverDatabaseSchema();
    try {
      await this.#recoveryPromise;
    } finally {
      this.#recoveryPromise = null;
    }
  }

  async #doRecoverDatabaseSchema(): Promise<void> {
    // 1) Best-effort delete to release old state (may be blocked by another tab)
    await new Promise<void>((resolve) => {
      try {
        const req = this.#indexedDB.deleteDatabase(this.#dbConfig.name);
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
      } catch (e) {
        // Ignore and continue with upgrade-based recovery
        resolve();
      }
    });

    // 2) Force an upgrade by bumping the version so onupgradeneeded fires even if delete was blocked
    await CreateObjectStore(
      this.#indexedDB,
      this.#dbConfig.name,
      this.#dbConfig.version,
      this.#dbConfig.objectStoresMeta,
      this.#dbConfig.migrationFactory,
    );
    // 3) Open the DB at the new version and verify all configured stores exist
    const db = await openDatabase(this.#indexedDB, this.#dbConfig.name, this.#dbConfig.version);
    const allStoresCreated = this.#dbConfig.objectStoresMeta.every((m) => db.objectStoreNames.contains(m.store));
    db.close();
    if (!allStoresCreated) {
      throw new Error('IndexedDB recovery failed: not all stores were created.');
    }
  }
  createDataBase() {
    if (!this.#dbConfig.name) {
      throw new Error('NgxIndexedDB: Please, provide the dbName in the configuration');
    }
    if (!this.#dbConfig.version) {
      throw new Error('NgxIndexedDB: Please, provide the db version in the configuration');
    }
    if (this.#isBrowser) {
      this.#indexedDB =
        window.indexedDB ||
        (window as any).mozIndexedDB ||
        (window as any).webkitIndexedDB ||
        (window as any).msIndexedDB;

      CreateObjectStore(
        this.#indexedDB,
        this.#dbConfig.name,
        this.#dbConfig.version,
        this.#dbConfig.objectStoresMeta,
        this.#dbConfig.migrationFactory,
      );

      openDatabase(this.#indexedDB, this.#dbConfig.name)
        .then((db) => {
          if (db.version !== this.#dbConfig.version) {
            if (
              typeof (globalThis as any).process !== 'undefined' &&
              (globalThis as any).process.env &&
              (globalThis as any).process.env.NODE_ENV !== 'production'
            ) {
            }
            this.#dbConfig.version = db.version;
          }
          // ANTI-HANG: CHIUDIAMO subito questa connessione di sola lettura-versione.
          // Prima restava APERTA per tutta la vita della pagina e BLOCCAVA ogni
          // upgrade di schema (`onblocked`) → recovery appesa → freeze. È solo un
          // controllo di versione: non serve tenerla viva.
          try {
            db.close();
          } catch {}
        })
        .catch(() => {
          // open fallita/timeout: ignoriamo (il controllo versione è best-effort).
        });
    }
  }
  /**
   * Allows to crate a new object store ad-hoc
   * @param storeName The name of the store to be created
   * @param migrationFactory The migration factory if exists
   */
  createObjectStore(
    storeSchema: ObjectStoreMeta,
    migrationFactory?: () => {
      [key: number]: (db: IDBDatabase, transaction: IDBTransaction) => void;
    },
  ): void {
    const storeSchemas: ObjectStoreMeta[] = [storeSchema];
    CreateObjectStore(this.#indexedDB, this.#dbConfig.name, this.#dbConfig.version, storeSchemas, migrationFactory);
  }

  /**
   * Adds new entry in the store and returns its key
   * @param storeName The name of the store to add the item
   * @param value The entry to be added
   * @param key The optional key for the entry
   */
  add<T>(storeName: string, value: T, key?: any): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db: IDBDatabase) => {
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const request: IDBRequest<IDBValidKey> = Boolean(key) ? objectStore.add(value, key) : objectStore.add(value);

          request.onsuccess = (evt: Event) => {
            const result = (evt.target as IDBOpenDBRequest).result;
            resolve(result as unknown as number);
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  bulkAdd<T>(storeName: string, values: T & { key?: any }[]): Promise<number[]> {
    return new Promise<number[]>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db: IDBDatabase) => {
          if (!values || values.length === 0) {
            resolve([]);
            return;
          }

          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const insertedKeys: number[] = [];
          let completedRequests = 0;
          let failed = false;

          const fail = (reason: any) => {
            if (failed) {
              return;
            }
            failed = true;
            reject(reason);
          };

          transaction.onerror = (event: Event) => {
            fail(event);
          };

          transaction.onabort = (event: Event) => {
            fail(event);
          };

          values.forEach((value) => {
            const valueWithOptionalKey = value as T & { key?: any };
            const key = valueWithOptionalKey.key;
            const { key: _unusedKey, ...payload } = (valueWithOptionalKey as any) ?? {};

            const request: IDBRequest<IDBValidKey> = Boolean(key)
              ? objectStore.add(payload as T, key)
              : objectStore.add(payload as T);

            request.onsuccess = (evt: Event) => {
              const result = (evt.target as IDBOpenDBRequest).result;
              insertedKeys.push(result as unknown as number);
              completedRequests += 1;
              if (completedRequests === values.length && !failed) {
                resolve(insertedKeys);
              }
            };

            request.onerror = (event: Event) => {
              fail(event);
            };
          });
        })
        .catch((reason) => reject(reason));
    });
  }

  addItem<T>(storeName: string, value: T, key?: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db: IDBDatabase) => {
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const hasKey = Boolean(key);
          const request: IDBRequest<IDBValidKey> = hasKey ? objectStore.add(value, key) : objectStore.add(value);

          request.onsuccess = (evt: Event) => {
            const result = (evt.target as IDBOpenDBRequest).result;
            const itemKey = hasKey ? key : (result as unknown as number);
            this.getByKey(storeName, itemKey).then((newValue) => {
              resolve(newValue as T);
            });
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  getByKey<T>(storeName: string, key: IDBValidKey): Promise<T> {
    // return from(
    return new Promise<T>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
        .then((db: IDBDatabase) => {
          const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const request = objectStore.get(key) as IDBRequest<T>;
          request.onsuccess = (event: Event) => {
            resolve((event.target as IDBRequest<T>).result);
          };
          request.onerror = (event: Event) => {
            reject(event);
          };
        })
        .catch((reason) => reject(reason));
    });
    // );
  }

  /**
   * Retrieve multiple entries in the store
   * @param storeName The name of the store to retrieve the items
   * @param keys The ids entries to be retrieve
   */

  bulkGet<T>(storeName: string, keys: Array<IDBValidKey>): Promise<T[]> {
    const promises = keys.map((key) => this.getByKey<T>(storeName, key));
    return Promise.all(promises);
  }

  /**
   * Returns entry by id.
   * @param storeName The name of the store to query
   * @param id The entry id
   */
  getByID<T>(storeName: string, id: string | number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
        .then((db: IDBDatabase) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const request: IDBRequest = objectStore.get(id) as IDBRequest<T>;
          request.onsuccess = (event: Event) => {
            resolve((event.target as IDBRequest<T>).result);
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns entry by index.
   * @param storeName The name of the store to query
   * @param indexName The index name to filter
   * @param key The entry key.
   */
  getByIndex<T>(storeName: string, indexName: string, key: IDBValidKey): Observable<T> {
    return from(
      new Promise<T>((resolve, reject) => {
        this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
          .then((db) => {
            validateBeforeTransaction(db, storeName, reject);
            const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
            const objectStore = transaction.objectStore(storeName);
            const index = objectStore.index(indexName);
            const request = index.get(key) as IDBRequest<T>;
            request.onsuccess = (event: Event) => {
              resolve((event.target as IDBRequest<T>).result);
            };
          })
          .catch((reason) => reject(reason));
      }),
    );
  }

  /**
   * Return all elements from one store
   * @param storeName The name of the store to select the items
   */
  private getAllObservable<T>(storeName: string): Observable<T[]> {
    return from(
      new Promise<T[]>((resolve, reject) => {
        this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
          .then((db) => {
            validateBeforeTransaction(db, storeName, reject);
            const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
            const objectStore = transaction.objectStore(storeName);

            const request: IDBRequest = objectStore.getAll();

            request.onerror = (evt: Event) => {
              reject(evt);
            };

            request.onsuccess = ({ target: { result: ResultAll } }: any) => {
              resolve(ResultAll as T[]);
            };
          })
          .catch((reason) => reject(reason));
      }),
    );
  }

  getAll<T>(storeName: string): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);

          const request: IDBRequest = objectStore.getAll();

          request.onerror = (evt: Event) => {
            reject(evt);
          };

          request.onsuccess = ({ target: { result: ResultAll } }: any) => {
            resolve(ResultAll as T[]);
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns all items from the store after update.
   * @param storeName The name of the store to update
   * @param value The new value for the entry
   * @param key The key of the entry to update if exists
   */
  update<T>(storeName: string, value: T, key?: any): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);

          transaction.oncomplete = () => {
            this.getAllObservable(storeName)
              .pipe(take(1))
              .subscribe((newValues) => {
                resolve(newValues as T[]);
              });
          };

          key ? objectStore.put(value, key) : objectStore.put(value);
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns the item you updated from the store after the update.
   * @param storeName The name of the store to update
   * @param value The new value for the entry
   * @param key The key of the entry to update
   */
  updateByKey<T>(storeName: string, value: T, key: IDBValidKey): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);

          transaction.oncomplete = () => {
            this.getByKey(storeName, key).then((newValue) => {
              resolve(newValue as T);
            });
          };

          objectStore.put(value, key);
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns all items from the store after delete.
   * @param storeName The name of the store to have the entry deleted
   * @param key The key of the entry to be deleted
   */
  delete<T>(storeName: string, key: Key): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          objectStore.delete(key as unknown as IDBValidKey);

          transaction.oncomplete = () => {
            this.getAllObservable(storeName)
              .pipe(take(1))
              .subscribe((newValues) => {
                resolve(newValues as T[]);
              });
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns true from the store after a successful delete.
   * @param storeName The name of the store to have the entry deleted
   * @param key The key of the entry to be deleted
   */
  deleteByKey(storeName: string, key: Key): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);

          transaction.oncomplete = () => {
            resolve(true);
          };

          objectStore.delete(key as unknown as IDBValidKey);
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns true if successfully delete all entries from the store.
   * @param storeName The name of the store to have the entries deleted
   */
  clear(storeName: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readwrite)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readwrite, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          objectStore.clear();
          transaction.oncomplete = () => {
            resolve(true);
          };
        })
        .catch((reason) => reject(reason));
    });
  }

  /**
   * Returns true if successfully delete the DB.
   */
  deleteDatabase(): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const db = await openDatabase(this.#indexedDB, this.#dbConfig.name, this.#dbConfig.version);
        await db.close();
        const deleteDBRequest = this.#indexedDB.deleteDatabase(this.#dbConfig.name);
        deleteDBRequest.onsuccess = () => {
          resolve(true);
        };
        deleteDBRequest.onerror = reject;
        deleteDBRequest.onblocked = () => {
          throw new Error(`Unable to delete database because it's blocked`);
        };
      } catch (evt) {
        reject(evt);
      }
    });
  }

  /**
   * Returns the open cursor event
   * @param storeName The name of the store to have the entries deleted
   * @param keyRange The key range which the cursor should be open on
   */
  openCursor(storeName: string, keyRange?: IDBKeyRange): Observable<Event> {
    return from(
      new Promise<Event>((resolve, reject) => {
        this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
          .then((db) => {
            validateBeforeTransaction(db, storeName, reject);
            const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
            const objectStore = transaction.objectStore(storeName);
            const request = keyRange === undefined ? objectStore.openCursor() : objectStore.openCursor(keyRange);

            request.onsuccess = (event: Event) => {
              resolve(event);
            };
          })
          .catch((reason) => reject(reason));
      }),
    );
  }

  /**
   * Open a cursor by index filter.
   * @param storeName The name of the store to query.
   * @param indexName The index name to filter.
   * @param keyRange The range value and criteria to apply on the index.
   */
  openCursorByIndex(
    storeName: string,
    indexName: string,
    keyRange: IDBKeyRange,
    mode: DBMode = DBMode.readonly,
  ): Observable<Event | null> {
    const obs = new Subject<Event | null>();

    this.#openDatabaseEnsuringStore(storeName, mode)
      .then((db) => {
        validateBeforeTransaction(db, storeName, (reason) => {
          obs.error(reason);
        });
        const transaction = createTransaction(
          db,
          optionsGenerator(
            mode,
            storeName,
            (reason) => {
              obs.error(reason);
            },
            () => {
              obs.next(null);
            },
          ),
        );
        const objectStore = transaction.objectStore(storeName);
        const index = objectStore.index(indexName);
        const request = index.openCursor(keyRange);

        request.onsuccess = (event: Event) => {
          obs.next(event);
        };
      })
      .catch((reason) => obs.error(reason));

    return obs;
  }

  /**
   * Returns all items by an index.
   * @param storeName The name of the store to query
   * @param indexName The index name to filter
   * @param keyRange  The range value and criteria to apply on the index.
   */
  getAllByIndex<T>(storeName: string, indexName: string, keyRange: IDBKeyRange): Observable<T[]> {
    const data: T[] = [];
    return from(
      new Promise<T[]>((resolve, reject) => {
        this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
          .then((db) => {
            validateBeforeTransaction(db, storeName, reject);
            const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
            const objectStore = transaction.objectStore(storeName);
            const index = objectStore.index(indexName);
            const request = index.openCursor(keyRange);
            request.onsuccess = (event) => {
              const cursor: IDBCursorWithValue = (event.target as IDBRequest<IDBCursorWithValue>).result;
              if (cursor) {
                data.push(cursor.value);
                cursor.continue();
              } else {
                resolve(data);
              }
            };
          })
          .catch((reason) => reject(reason));
      }),
    );
  }

  /**
   * Returns all primary keys by an index.
   * @param storeName The name of the store to query
   * @param indexName The index name to filter
   * @param keyRange  The range value and criteria to apply on the index.
   */
  getAllKeysByIndex(
    storeName: string,
    indexName: string,
    keyRange: IDBKeyRange,
  ): Observable<{ primaryKey: any; key: any }[]> {
    const data: { primaryKey: any; key: any }[] = [];
    return from(
      new Promise<{ primaryKey: any; key: any }[]>((resolve, reject) => {
        this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
          .then((db) => {
            validateBeforeTransaction(db, storeName, reject);
            const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
            const objectStore = transaction.objectStore(storeName);
            const index = objectStore.index(indexName);
            const request = index.openKeyCursor(keyRange);
            request.onsuccess = (event) => {
              const cursor: IDBCursor = (event.target as IDBRequest<IDBCursor>).result;
              if (cursor) {
                data.push({ primaryKey: cursor.primaryKey, key: cursor.key });
                cursor.continue();
              } else {
                resolve(data);
              }
            };
          })
          .catch((reason) => reject(reason));
      }),
    );
  }

  /**
   * Returns the number of rows in a store.
   * @param storeName The name of the store to query
   * @param keyRange  The range value and criteria to apply.
   */
  count(storeName: string, keyRange?: IDBValidKey | IDBKeyRange): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      this.#openDatabaseEnsuringStore(storeName, DBMode.readonly)
        .then((db) => {
          validateBeforeTransaction(db, storeName, reject);
          const transaction = createTransaction(db, optionsGenerator(DBMode.readonly, storeName, reject, resolve));
          const objectStore = transaction.objectStore(storeName);
          const request: IDBRequest = objectStore.count(keyRange);
          request.onerror = (e) => reject(e);
          request.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result as unknown as number);
        })
        .catch((reason) => reject(reason));
    });
  }
}
